import {
  AddUniversal, CloseUniversal, PlaybackSpeedUniversal, TickMarkOutlined, TimerUniversal,
  UndoUniversal, WarningOutlined,
} from '@duxton-web/icons';
import {
  Alert, Button, Card, Empty, Flex, Input, Modal, Select, Table, Tabs, Tag,
  Switch, Tooltip, Typography,
} from '@duxton-web/ui';
import i18n from 'i18n-js';
import { AudienceSourceType } from 'omega-rtc/types/ts/content-config';
import React, { useEffect, useRef, useState } from 'react';

import {
  MAX_AUDIENCE_REASON_LENGTH, PRODUCT_LANG_FIELDS, audienceReasonLength,
  formatDateTime, isAudienceLangValid, isAudienceReasonValid, langLabel as LANG_LABEL,
  localeSorter,
  nameFromEmail, normalizeAudienceReason, normalizeProductAudienceAssociations,
  normalizeProductDeeplink,
  productDisplayName,
} from './adapters';
import DeeplinkQr from './DeeplinkQr';
import DiffField, { DiffChips } from './DiffField';
import { editChangesText } from './editChanges';
import { FunnelObjectivesCard } from './FunnelObjectives';
import {
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitProductForApproval,
} from './permissions';
import { PRODUCT_STATUS_TAG, contentConfigModalStyle } from './theme';

const { TextArea } = Input;
const AUDIENCE_LOCALE = 'ucd_content_config.product_offerings.audiences';
const audienceText = (key, options = {}) => i18n.t(`${AUDIENCE_LOCALE}.${key}`, options);
const PRODUCT_OFFERING_LOCALE = 'ucd_content_config.product_offerings';
const productOfferingText = (key, options = {}) =>
  i18n.t(`${PRODUCT_OFFERING_LOCALE}.${key}`, options);

export const audienceAssociationTagColor = (option) => {
  const regional = !option || !option.country;
  if (option && option.sourceType === AudienceSourceType.Table) {
    return regional ? 'purple' : 'magenta';
  }
  return regional ? 'cyan' : 'blue';
};

export const FieldLabel = ({ children, required }) => (
  <Typography.Body level={2} strong className="cc-field-label">
    {children}
    {required ? ' *' : ''}
  </Typography.Body>
);

// Marks a Product Offering value that is not its own but inherited, which
// Content Injection falls back to when the offering defines none:
// - `vertical`: the deep link (campaignGenerator.getDeeplink ->
//   GetContentInjectionDeeplink);
// - `country`: an Audience association's language (effectiveAudienceLanguage).
// The inherited value itself is only ever shown as a placeholder or beside a
// diff row, never as a control's value or a diff side, so it can neither be
// saved onto the offering nor read as a pending change.
export const DefaultTag = ({ source }: { source: 'vertical' | 'country' }) => (
  <Tag color="warning" className="cc-default-tag">
    {productOfferingText(`tags.${source}_default`)}
  </Tag>
);

const CtaInput = ({ value, onChange, disabled }) => {
  const items = (value || '').split('\n').map((s) => s.trim()).filter(Boolean);
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v].join('\n'));
    setDraft('');
  };
  return (
    <div>
      {items.length > 0 && (
        <Flex gap={6} wrap="wrap" className={disabled ? '' : 'cc-cta-list'}>
          {items.map((c, i) => (
            <Tag key={`${c}-${i}`} closable={!disabled} onClose={() => onChange(items.filter((_, j) => j !== i).join('\n'))}>{c}</Tag>
          ))}
        </Flex>
      )}
      {!disabled && (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={add}
          placeholder="Type a CTA and press Enter"
          suffix={<Button type="text" size="small" shape="circle" icon={<AddUniversal />} onClick={add} />}
        />
      )}
    </div>
  );
};

export const ProductOfferingList = ({
  canEditLocally,
  country,
  countries,
  listTab,
  loading,
  onCountryChange,
  onCreate,
  onOpenDetail,
  rows,
  setListTab,
  setVerticalFilter,
  verticalFilter,
  verticalName,
  verticals,
}) => {
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: localeSorter('name'),
      render: (name, row) => (
        <Flex align="center" gap={6}>
          <Typography.Body level={2}>{name}</Typography.Body>
          {row.status === 'pending' && (
            <Tooltip title="Pending approval">
              <TimerUniversal className="cc-pending-icon" />
            </Tooltip>
          )}
          {row.status === 'draft' && <Tag color="warning">DRAFT</Tag>}
        </Flex>
      ),
    },
    {
      title: 'Vertical',
      dataIndex: 'vertical',
      render: (v) => <Typography.Body level={2}>{verticalName(v)}</Typography.Body>,
    },
    {
      title: 'CI Languages',
      dataIndex: 'ciEnabledLangs',
      width: 220,
      render: (enabledLangs) => ((enabledLangs || []).length ? (
        <Flex gap={4} wrap="wrap">
          {(enabledLangs || []).map((lang) => <Tag color="success" key={lang}>{LANG_LABEL(lang)}</Tag>)}
        </Flex>
      ) : (
        <Tag>NONE</Tag>
      )),
    },
    {
      title: 'Updated by',
      dataIndex: 'updatedBy',
      render: (u) => <Typography.Body level={2} type="secondary">{nameFromEmail(u) || '—'}</Typography.Body>,
    },
    {
      title: 'Updated on',
      dataIndex: 'updatedAt',
      width: 200,
      render: (t) => <Typography.Body level={2} type="secondary">{formatDateTime(t)}</Typography.Body>,
    },
  ];

  return (
    <Flex vertical gap="middle">
      <Flex align="center" gap="middle" wrap="wrap">
        <Typography.Title level={4} className="cc-title-reset">Product Offerings</Typography.Title>
        <Tooltip title={canEditLocally ? '' : "You don't have permission to edit product offerings."}>
          <Button type="primary" icon={<AddUniversal />} disabled={!canEditLocally} onClick={onCreate}>
            Create
          </Button>
        </Tooltip>
      </Flex>

      <Tabs
        type="pill"
        activeKey={listTab}
        onChange={setListTab}
        items={[
          { key: 'all', label: 'All' },
          { key: 'pending', label: 'Pending Approval' },
          { key: 'ci-enabled', label: 'Content Injection' },
        ]}
      />

      <Flex align="center" gap={8} wrap="wrap" className="cc-list-controls">
        <Typography.Body level={2} type="secondary">Country</Typography.Body>
        <Select
          value={country}
          onChange={onCountryChange}
          className="cc-country-select"
          options={countries.map((c) => ({ value: c.id, label: c.name }))}
        />
        <Typography.Body level={2} type="secondary">Vertical</Typography.Body>
        <Select
          value={verticalFilter}
          onChange={setVerticalFilter}
          className="cc-vertical-select"
          options={[
            { value: '', label: 'All' },
            ...verticals.map((v) => ({ value: v.id, label: v.name })),
          ]}
        />
      </Flex>

      <Table
        rowKey="key"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        onRow={(r) => ({ onClick: () => onOpenDetail(r.key), className: 'cc-row-clickable' })}
        locale={{
          emptyText: {
            pending: 'No product offerings pending approval in this country.',
            'ci-enabled': 'No CI enabled product offerings in this country.',
          }[listTab] || 'No product offerings in this country yet.',
        }}
      />
    </Flex>
  );
};

export const AudienceConfigurationModal = ({
  ciEnabledLangs = [],
  disabled = false,
  failed = false,
  inheritedLang = '',
  langOptions = [],
  loaded = false,
  loading = false,
  onApply,
  onClose,
  onRetry,
  open,
  options = [],
  value = [],
}) => {
  const [draftAssociations, setDraftAssociations] = useState([]);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setDraftAssociations(normalizeProductAudienceAssociations(value));
    }
    wasOpen.current = open;
  }, [open, value]);

  const selectedKeys = draftAssociations.map(({ audienceKey }) => audienceKey);
  const optionByKey = new Map(options.map((option) => [option.key, option]));
  const visibleOptions = options.filter(
    (option) => option.eligible || selectedKeys.includes(option.key),
  );
  const eligibleCount = options.filter((option) => option.eligible).length;
  const hasInvalidReferences = loaded && draftAssociations.some(({ audienceKey }) => {
    const option = optionByKey.get(audienceKey);
    return !option || !option.eligible;
  });
  const hasInvalidReasons = draftAssociations.some(
    ({ reasonToUse }) => !isAudienceReasonValid(reasonToUse),
  );
  // Only checkable once the country's whitelist is known; an empty list means
  // the country config has not loaded or configures no languages, and the
  // Select is rendered disabled instead.
  const hasInvalidLangs = langOptions.length > 0 && draftAssociations.some(
    ({ lang }) => !isAudienceLangValid(lang, langOptions),
  );

  const setAudienceKeys = (keys) => {
    const associationByKey = new Map(
      draftAssociations.map((association) => [association.audienceKey, association]),
    );
    setDraftAssociations(keys.map((audienceKey) => (
      associationByKey.get(audienceKey) || { audienceKey, reasonToUse: '', lang: '' }
    )));
  };
  const setAudienceReason = (audienceKey, reasonToUse) => {
    setDraftAssociations(draftAssociations.map((association) => (
      association.audienceKey === audienceKey
        ? { ...association, reasonToUse }
        : association
    )));
  };
  // A language the country configures but this Product Offering does not have
  // CI enabled for is accepted by UCD and saved, but yields no CI campaigns —
  // a warning, never a blocker.
  const isLangWithoutCi = (lang) => Boolean(lang)
    && isAudienceLangValid(lang, langOptions)
    && !ciEnabledLangs.includes(lang);

  // `lang` is stored exactly as chosen (or '' when cleared): UCD validates by
  // exact string match and never normalises.
  const setAudienceLang = (audienceKey, lang) => {
    const next = lang || '';
    if (isLangWithoutCi(next)) {
      Modal.info({
        title: audienceText('lang_not_ci_title'),
        content: (
          <Flex vertical gap={8}>
            <Typography.Body level={2}>
              {audienceText('lang_not_ci_message', { language: LANG_LABEL(next) })}
            </Typography.Body>
            <Typography.Body level={2} type="secondary">
              {audienceText('lang_not_ci_hint', { language: LANG_LABEL(next) })}
            </Typography.Body>
          </Flex>
        ),
      });
    }
    setDraftAssociations(draftAssociations.map((association) => (
      association.audienceKey === audienceKey
        ? { ...association, lang: next }
        : association
    )));
  };
  const apply = () => {
    if (hasInvalidReferences || hasInvalidReasons || hasInvalidLangs) return;
    onApply(draftAssociations);
  };

  return (
    <Modal
      title={audienceText('details')}
      open={open}
      onCancel={onClose}
      width={1200}
      style={contentConfigModalStyle}
      className="cc-modal cc-audience-modal"
      footer={(
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>
            {disabled ? audienceText('close') : audienceText('cancel')}
          </Button>
          {!disabled && (
            <Button
              type="primary"
              disabled={hasInvalidReferences || hasInvalidReasons || hasInvalidLangs}
              onClick={apply}
            >
              {audienceText('apply')}
            </Button>
          )}
        </Flex>
      )}
    >
      <Flex vertical gap="middle">
        {loading && (
          <Typography.Body level={2} type="secondary">
            {audienceText('loading')}
          </Typography.Body>
        )}
        {failed && (
          <Alert
            type="error"
            showIcon
            message={(
              <Flex justify="space-between" align="center" gap={8} wrap="wrap">
                <span>{audienceText('error')}</span>
                <Button size="small" onClick={onRetry}>
                  {audienceText('retry')}
                </Button>
              </Flex>
            )}
          />
        )}
        {loaded && eligibleCount === 0 && (
          <Typography.Body level={2} type="secondary">
            {audienceText('empty')}
          </Typography.Body>
        )}

        <div>
          <FieldLabel>{audienceText('selection_label')}</FieldLabel>
          <Select
            mode="multiple"
            value={selectedKeys}
            onChange={setAudienceKeys}
            options={visibleOptions.map((option) => ({
              value: option.key,
              label: option.eligible
                ? option.label
                : `${option.label} · ${audienceText('unavailable')}`,
            }))}
            placeholder={audienceText('add_placeholder')}
            allowClear
            loading={loading}
            disabled={disabled || loading || failed || !loaded}
            className="cc-full-width"
            optionFilterProp="label"
            optionRender={(selectOption) => {
              const option = optionByKey.get(String(selectOption.value));
              if (!option) return selectOption.label;
              const fullLabel = `${option.label}${option.eligible
                ? ''
                : ` · ${audienceText('unavailable')}`}`;
              return (
                <Tag
                  color={option.eligible
                    ? audienceAssociationTagColor(option)
                    : 'error'}
                  className="cc-audience-dropdown-option"
                >
                  <span className="cc-audience-dropdown-option-label">
                    {fullLabel}
                  </span>
                </Tag>
              );
            }}
            tagRender={({
              label, closable, onClose: onTagClose, value: optionValue,
            }) => {
              const option = optionByKey.get(String(optionValue));
              const fullLabel = option
                ? `${option.label}${option.eligible ? '' : ` · ${audienceText('unavailable')}`}`
                : String(label);
              const chipLabel = option
                ? option.name || option.key
                : String(label);
              return (
                <Tooltip
                  title={fullLabel}
                  key={String(optionValue)}
                >
                  <Tag
                    color={option && !option.eligible
                      ? 'error'
                      : audienceAssociationTagColor(option)}
                    closable={closable}
                    onClose={onTagClose}
                    className="cc-tag-gap cc-audience-selection-tag"
                  >
                    <span className="cc-audience-selection-tag-label">{chipLabel}</span>
                  </Tag>
                </Tooltip>
              );
            }}
          />
        </div>

        {draftAssociations.length === 0 && (
          <Empty description={audienceText('no_selection')} />
        )}
        {draftAssociations.length > 0 && (
          <div className="cc-audience-association-grid">
            {draftAssociations.map(({ audienceKey, reasonToUse, lang }) => {
              const option = optionByKey.get(audienceKey);
              const displayLabel = option
                ? option.label
                : `${audienceText('unavailable_prefix')} · ${audienceKey}`;
              const reasonLength = audienceReasonLength(reasonToUse);
              const reasonValid = isAudienceReasonValid(reasonToUse);
              const langConfigured = langOptions.length > 0;
              const langValid = !langConfigured || isAudienceLangValid(lang, langOptions);
              const langWithoutCi = langConfigured && isLangWithoutCi(lang);
              // An untargeted association generates in the country default
              // language. Shown as the placeholder only, so it is never
              // written into the association.
              const showsCountryDefault = langConfigured && !lang && Boolean(inheritedLang);
              // A stored language the country no longer configures stays
              // selectable so the user can see and fix it; it is never dropped
              // or rewritten.
              const langSelectOptions = langOptions.map((code) => ({
                value: code,
                label: LANG_LABEL(code),
              }));
              if (langConfigured && lang && !langOptions.includes(lang)) {
                langSelectOptions.push({
                  value: lang,
                  label: `${lang} · ${audienceText('lang_unavailable')}`,
                });
              }
              return (
                <div
                  className={`cc-audience-association${option && !option.eligible ? ' cc-audience-association--invalid' : ''}`}
                  key={audienceKey}
                >
                  <Flex align="start" justify="space-between" gap={6} wrap="nowrap">
                    <Flex align="center" gap={6} wrap="wrap">
                      <Tooltip title={displayLabel}>
                        <Tag
                          color={option && option.eligible
                            ? audienceAssociationTagColor(option)
                            : 'error'}
                          className="cc-audience-association-heading"
                        >
                          <span className="cc-audience-association-heading-label">
                            {displayLabel}
                          </span>
                        </Tag>
                      </Tooltip>
                      {option && !option.eligible && (
                        <Tag color="error">{audienceText('unavailable')}</Tag>
                      )}
                    </Flex>
                    {langWithoutCi && (
                      <Tooltip
                        title={audienceText('lang_not_ci_message', { language: LANG_LABEL(lang) })}
                      >
                        <span
                          className="cc-audience-lang-warning"
                          aria-label={audienceText('lang_not_ci_title')}
                        >
                          <WarningOutlined />
                        </span>
                      </Tooltip>
                    )}
                  </Flex>
                  {option && !option.eligible && loaded && (
                    <Typography.Body level={2} type="danger">
                      {audienceText('invalid_reference')}
                    </Typography.Body>
                  )}
                  <div className="cc-audience-lang">
                    <Flex align="center" gap={4} wrap="wrap" className="cc-audience-lang-heading">
                      <Typography.Body
                        level={3}
                        strong
                        className="cc-audience-lang-label"
                      >
                        {audienceText('lang_label')}
                      </Typography.Body>
                      {showsCountryDefault && <DefaultTag source="country" />}
                    </Flex>
                    <Select
                      aria-label={`${audienceText('lang_label')}: ${displayLabel}`}
                      value={lang || undefined}
                      onChange={(value) => setAudienceLang(audienceKey, value)}
                      options={langSelectOptions}
                      placeholder={(!langConfigured && audienceText('lang_unconfigured'))
                        || (inheritedLang && LANG_LABEL(inheritedLang))
                        || audienceText('lang_placeholder')}
                      allowClear
                      disabled={disabled || !langConfigured}
                      status={langValid ? undefined : 'error'}
                      className="cc-full-width"
                      optionFilterProp="label"
                    />
                    <Typography.Metadata
                      type={(langValid && !langWithoutCi && 'secondary')
                        || (langValid && 'warning')
                        || 'danger'}
                    >
                      {(!langValid && audienceText('lang_invalid_reference'))
                        || (langWithoutCi && audienceText('lang_not_ci_short'))
                        || audienceText('lang_help')}
                    </Typography.Metadata>
                  </div>
                  <div>
                    <Typography.Body
                      level={3}
                      strong
                      className="cc-audience-reason-label"
                    >
                      {audienceText('reason_label')}
                    </Typography.Body>
                    <TextArea
                      aria-label={`${audienceText('reason_label')}: ${displayLabel}`}
                      value={reasonToUse}
                      onChange={(event) => setAudienceReason(audienceKey, event.target.value)}
                      placeholder={audienceText('reason_help')}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      disabled={disabled}
                      status={reasonValid ? undefined : 'error'}
                    />
                    <Flex
                      justify={reasonValid ? 'flex-end' : 'space-between'}
                      align="start"
                      gap={8}
                    >
                      {!reasonValid && (
                        <Typography.Metadata type="danger">
                          {audienceText('reason_too_long', {
                            max: MAX_AUDIENCE_REASON_LENGTH,
                          })}
                        </Typography.Metadata>
                      )}
                      <Typography.Metadata type={reasonValid ? 'secondary' : 'danger'}>
                        {reasonLength}/{MAX_AUDIENCE_REASON_LENGTH}
                      </Typography.Metadata>
                    </Flex>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Flex>
    </Modal>
  );
};

const ProductLinks = ({
  attrOptions,
  audienceCiEnabledLangs = [],
  audienceInheritedLang = '',
  audienceLangOptions = [],
  audienceListFailed = false,
  audienceListLoaded = false,
  audienceListLoading = false,
  audienceOptions = [],
  audiencesEnabled = false,
  cvpOptions,
  disabled,
  listVal,
  objectivesEnabled = false,
  onRetryAudiences,
  personaOptions,
  setList,
}) => {
  const [audienceModalOpen, setAudienceModalOpen] = useState(false);
  const linkCard = ({
    title, color, field, options,
  }) => {
    const selected = listVal(field);
    return (
      <Card bordered={false}>
        <Flex vertical gap="small">
          <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
            <Typography.Body level={2} strong className="cc-title-reset">{title}</Typography.Body>
            <Tag>{selected.length} of {options.length}</Tag>
          </Flex>
          <Select
            mode="multiple"
            value={selected}
            onChange={(vals) => setList(field, vals)}
            options={options.map((o) => ({ value: o.key, label: o.key }))}
            placeholder="Add..."
            allowClear
            disabled={disabled}
            className="cc-full-width"
            optionFilterProp="label"
            tagRender={({ label, closable, onClose }) => (
              <Tag color={color} closable={closable} onClose={onClose} className="cc-tag-gap">
                {label}
              </Tag>
            )}
          />
        </Flex>
      </Card>
    );
  };

  const audienceAssociations = normalizeProductAudienceAssociations(listVal('audiences'));
  const audienceOptionByKey = new Map(audienceOptions.map((option) => [option.key, option]));

  return (
    <Flex vertical gap="middle">
      {linkCard({
        title: 'CVP', color: 'volcano', field: 'cvps', options: cvpOptions,
      })}
      {!audiencesEnabled && linkCard({
        title: 'Attribute', color: 'blue', field: 'attrs', options: attrOptions,
      })}
      {linkCard({
        title: 'Persona', color: 'purple', field: 'personas', options: personaOptions,
      })}
      {audiencesEnabled && (
        <Card bordered={false}>
          <Flex vertical gap="small">
            <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
              <Flex align="center" gap={8}>
                <Typography.Body level={2} strong className="cc-title-reset">
                  {audienceText('title')}
                </Typography.Body>
                <Tag>{audienceAssociations.length}</Tag>
              </Flex>
              <Button size="small" onClick={() => setAudienceModalOpen(true)}>
                {audienceText('details')}
              </Button>
            </Flex>
            {audienceAssociations.length === 0 ? (
              <Typography.Body level={2} type="secondary">
                {audienceText('not_configured')}
              </Typography.Body>
            ) : (
              <Flex gap={6} wrap="wrap" className="cc-audience-chip-list">
                {audienceAssociations.map(({ audienceKey, lang }) => {
                  const option = audienceOptionByKey.get(audienceKey);
                  const langSuffix = lang ? ` · ${LANG_LABEL(lang)}` : '';
                  const fullLabel = `${option
                    ? `${option.label}${option.eligible
                      ? ''
                      : ` · ${audienceText('unavailable')}`}`
                    : `${audienceText('unavailable_prefix')} · ${audienceKey}`}${langSuffix}`;
                  const chipLabel = `${option
                    ? option.name || option.key
                    : audienceKey}${langSuffix}`;
                  return (
                    <Tooltip title={fullLabel} key={audienceKey}>
                      <Tag
                        color={option && option.eligible
                          ? audienceAssociationTagColor(option)
                          : 'error'}
                        className="cc-audience-summary-chip"
                      >
                        <span className="cc-audience-summary-chip-label">
                          {chipLabel}
                        </span>
                      </Tag>
                    </Tooltip>
                  );
                })}
              </Flex>
            )}
          </Flex>
          <AudienceConfigurationModal
            ciEnabledLangs={audienceCiEnabledLangs}
            disabled={disabled}
            failed={audienceListFailed}
            inheritedLang={audienceInheritedLang}
            langOptions={audienceLangOptions}
            loaded={audienceListLoaded}
            loading={audienceListLoading}
            onApply={(associations) => {
              setList('audiences', associations);
              setAudienceModalOpen(false);
            }}
            onClose={() => setAudienceModalOpen(false)}
            onRetry={onRetryAudiences}
            open={audienceModalOpen}
            options={audienceOptions}
            value={audienceAssociations}
          />
        </Card>
      )}
      {objectivesEnabled && (
        <FunnelObjectivesCard
          disabled={disabled}
          objectives={listVal('objectives')}
          onChange={(next) => setList('objectives', next)}
        />
      )}
    </Flex>
  );
};

const ProductLanguageTabs = ({
  activeLang,
  disabled,
  langVal,
  langs,
  setActiveLang,
  setLang,
}) => {
  const hasRequiredContent = (code) => Boolean(
    String(langVal(code, 'name') || '').trim()
    && String(langVal(code, 'overview') || '').trim(),
  );
  const isCIEnabled = (code) => hasRequiredContent(code) && Boolean(langVal(code, 'ciEnabled'));

  return (
    <Card bordered={false} className="cc-section-gap">
      <Tabs
        activeKey={activeLang}
        onChange={setActiveLang}
        type="pill"
        tabBarExtraContent={{
          right: <Tag>{langs.filter((c) => langVal(c, 'name').trim()).length} of {langs.length} filled</Tag>,
        }}
        items={langs.map((code) => ({
          key: code,
          label: (
            <Flex align="center" gap={4}>
              <span>{LANG_LABEL(code)}</span>
              {langVal(code, 'name').trim() ? <TickMarkOutlined /> : null}
            </Flex>
          ),
          children: (
            <Flex vertical gap={16}>
              <div className="cc-lang-name-row">
                <div>
                  <FieldLabel required>Name</FieldLabel>
                  <Input
                    value={langVal(code, 'name')}
                    onChange={(e) => setLang(code, 'name', e.target.value)}
                    placeholder={`Localized product name in ${LANG_LABEL(code)}...`}
                    disabled={disabled}
                  />
                </div>
                <div className="cc-lang-ci-field">
                  <FieldLabel>CI Enabled</FieldLabel>
                  <Flex align="center" gap={8} className="cc-lang-ci-toggle">
                    <Switch
                      checked={isCIEnabled(code)}
                      onChange={(checked) => setLang(code, 'ciEnabled', hasRequiredContent(code) && checked)}
                      disabled={disabled || !hasRequiredContent(code)}
                    />
                    <Typography.Body level={2} type="secondary">
                      {isCIEnabled(code) ? 'Enabled' : 'Disabled'}
                    </Typography.Body>
                  </Flex>
                </div>
              </div>
              <div>
                <FieldLabel required>Product overview</FieldLabel>
                <TextArea value={langVal(code, 'overview')} onChange={(e) => setLang(code, 'overview', e.target.value)} autoSize={{ minRows: 3, maxRows: 10 }} disabled={disabled} />
              </div>
              <div>
                <FieldLabel>Reason to use</FieldLabel>
                <TextArea value={langVal(code, 'reason')} onChange={(e) => setLang(code, 'reason', e.target.value)} autoSize={{ minRows: 3, maxRows: 12 }} disabled={disabled} />
              </div>
              <div>
                <FieldLabel>Call to action</FieldLabel>
                <CtaInput value={langVal(code, 'cta')} onChange={(v) => setLang(code, 'cta', v)} disabled={disabled} />
              </div>
            </Flex>
          ),
        }))}
      />
    </Card>
  );
};

const ProductDiffModal = ({
  audienceLabelForKey = (key) => key,
  pendingActionsDisabledReason = '',
  audiencesEnabled = false,
  busy,
  canReview,
  canUpdate,
  comparisonDiff,
  generationDisabledReason,
  inheritedLang = '',
  isMine,
  langs,
  objectivesEnabled = false,
  onApprove,
  onCancel,
  onGenerate,
  onEditChanges,
  onRecall,
  onReject,
  open,
  sel,
  verticalDeeplink = () => '',
  verticalName,
}) => {
  const isDraftDiff = Boolean(sel && sel.draft && comparisonDiff && !sel.active && !sel.pending);
  const hasLocalEdits = Boolean(sel && sel.active && comparisonDiff);
  const hasPendingVersion = Boolean(sel && sel.pending);
  // Only ambiguous when both exist; default to the local edits, since those are
  // the changes the user just made and cannot see anywhere else.
  const [compareLocalEdits, setCompareLocalEdits] = useState(true);
  useEffect(() => {
    if (open) setCompareLocalEdits(true);
  }, [open]);
  const canSwitchComparison = hasLocalEdits && hasPendingVersion;
  const isLocalDiff = hasLocalEdits && (!hasPendingVersion || compareLocalEdits);
  const a = isDraftDiff
    ? {
      name: '',
      vertical: '',
      deeplink: '',
      byLang: {},
      whitelist: {
        cvps: [], attrs: [], personas: [], audiences: [], objectives: [],
      },
    }
    : sel && sel.active;
  const p = isDraftDiff || isLocalDiff ? comparisonDiff : sel && sel.pending;
  const activeLabel = isDraftDiff ? 'CURRENT' : 'ACTIVE';
  const activeTagColor = isDraftDiff ? undefined : 'error';
  const pendingTagColor = isDraftDiff || isLocalDiff ? 'warning' : 'success';
  // Titles name the versions being compared, matching the column tags.
  let pendingLabel = 'PENDING APPROVAL';
  let title = 'Pending approval vs ACTIVE';
  if (isDraftDiff) {
    pendingLabel = 'DRAFT';
    title = 'Draft vs current';
  } else if (isLocalDiff) {
    pendingLabel = 'LOCAL EDITS';
    title = 'Local edits vs ACTIVE';
  }
  const canRecallChange = canRecallPendingChange({ canUpdate }, isMine);
  const canReviewChange = canReviewPendingChange({ canReview }, isMine);

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={sel && sel.pending ? (
        <Flex justify="flex-end" gap={8}>
          <Tooltip title={generationDisabledReason}>
            <Button
              size="small"
              icon={<PlaybackSpeedUniversal />}
              disabled={Boolean(generationDisabledReason)}
              onClick={() => { onCancel(); onGenerate(); }}
            >
              Generate content
            </Button>
          </Tooltip>
          {canRecallChange ? (
            <React.Fragment>
              <Tooltip title={pendingActionsDisabledReason}>
                <Button
                  size="small"
                  icon={<UndoUniversal />}
                  danger
                  loading={busy}
                  disabled={Boolean(pendingActionsDisabledReason)}
                  onClick={() => { onCancel(); onRecall(); }}
                >
                  Recall
                </Button>
              </Tooltip>
              <Tooltip title={pendingActionsDisabledReason}>
                <Button
                  size="small"
                  loading={busy}
                  disabled={Boolean(pendingActionsDisabledReason)}
                  onClick={() => { onCancel(); onEditChanges(); }}
                >
                  {editChangesText('action')}
                </Button>
              </Tooltip>
            </React.Fragment>
          ) : null}
          {canReviewChange ? (
            <React.Fragment>
              <Button size="small" type="primary" danger icon={<CloseUniversal />} loading={busy} onClick={() => { onCancel(); onReject(); }}>Reject</Button>
              <Button size="small" type="primary" icon={<TickMarkOutlined />} loading={busy} onClick={() => { onCancel(); onApprove(); }}>Approve</Button>
            </React.Fragment>
          ) : null}
        </Flex>
      ) : null}
      width={1472}
      style={contentConfigModalStyle}
      className="cc-modal"
    >
      {a && p && (() => {
        const lv = (rec, lang, f) => {
          const byLang = (((rec && rec.byLang) || {})[lang] || {});
          const raw = byLang[f.key];
          if (f.type === 'boolean') {
            return raw && byLang.name && byLang.name.trim() && byLang.overview && byLang.overview.trim()
              ? 'Enabled'
              : 'Disabled';
          }
          return raw || '';
        };
        const langChanged = (lang) => PRODUCT_LANG_FIELDS.some((f) => lv(p, lang, f) !== lv(a, lang, f));
        // One line per objective for the line-mode diff; the UCD-owned id is
        // an internal identifier and stays out of the rendered diff. Newlines
        // inside a description are flattened so one objective is always one
        // diff line.
        const singleLine = (value) => String(value || '').replace(/\s*\n\s*/g, ' ');
        const objectiveLines = (rec) => (((rec && rec.whitelist) || {}).objectives || [])
          .map((objective) => `${singleLine(objective.name)} — ${singleLine(objective.description)} — ${objective.source}`)
          .join('\n');
        // Neither version sets a deep link -> both fall back to the Vertical's.
        // Resolved from the proposed version's vertical so a vertical change in
        // the same diff shows the default that would then apply.
        const inheritedDeeplink = !a.deeplink && !p.deeplink
          ? verticalDeeplink(p.vertical || a.vertical)
          : '';
        const activeAudiences = normalizeProductAudienceAssociations(
          (a.whitelist && a.whitelist.audiences) || [],
        );
        const pendingAudiences = normalizeProductAudienceAssociations(
          (p.whitelist && p.whitelist.audiences) || [],
        );
        const activeAudienceByKey = new Map(
          activeAudiences.map((association) => [association.audienceKey, association]),
        );
        const changedAudienceReasons = pendingAudiences.filter((association) => {
          const activeAssociation = activeAudienceByKey.get(association.audienceKey);
          const activeReason = normalizeAudienceReason(
            activeAssociation ? activeAssociation.reasonToUse : '',
          );
          const pendingReason = normalizeAudienceReason(association.reasonToUse);
          return activeReason !== pendingReason;
        });
        // Languages are compared verbatim, so an added, changed and cleared
        // language each surface as their own diff row.
        const changedAudienceLangs = pendingAudiences.filter((association) => {
          const activeAssociation = activeAudienceByKey.get(association.audienceKey);
          return (activeAssociation ? activeAssociation.lang : '') !== association.lang;
        });
        return (
          <div className="cc-diff">
            {canSwitchComparison && (
              // The modal title already names the current comparison.
              <Flex justify="flex-end" className="cc-diff-comparison-switch">
                <Button
                  size="small"
                  onClick={() => setCompareLocalEdits((current) => !current)}
                >
                  {isLocalDiff ? 'Compare pending approval' : 'Compare local edits'}
                </Button>
              </Flex>
            )}
            <DiffField
              label="Tagged Name"
              activeLabel={activeLabel}
              activeTagColor={activeTagColor}
              pendingLabel={pendingLabel}
              pendingTagColor={pendingTagColor}
              activeVal={a.name || ''}
              pendingVal={p.name || ''}
            />
            <DiffField
              label="Vertical"
              activeLabel={activeLabel}
              activeTagColor={activeTagColor}
              pendingLabel={pendingLabel}
              pendingTagColor={pendingTagColor}
              activeVal={verticalName(a.vertical)}
              pendingVal={verticalName(p.vertical)}
            />
            <DiffField
              label={productOfferingText('fields.deeplink')}
              activeLabel={activeLabel}
              activeTagColor={activeTagColor}
              pendingLabel={pendingLabel}
              pendingTagColor={pendingTagColor}
              activeVal={a.deeplink || ''}
              pendingVal={p.deeplink || ''}
            />
            {/* Annotation only, and only when neither version sets a deep link:
                the DiffField values stay raw so an unchanged blank field never
                renders as an add or an edit. Resolved from the proposed
                version's vertical, so a vertical change in the same diff shows
                the default that would then apply. */}
            {Boolean(inheritedDeeplink) && (
              <Flex align="center" gap={4} wrap="wrap" className="cc-deeplink-default">
                <DefaultTag source="vertical" />
                <Typography.Metadata>{inheritedDeeplink}</Typography.Metadata>
              </Flex>
            )}
            <div className="cc-diff-language-section">
              <Tabs
                type="pill"
                items={langs.map((lang) => ({
                  key: lang,
                  label: (
                    <Flex align="center" gap={4}>
                      {langChanged(lang) ? (
                        <WarningOutlined className="cc-diff-language-warning" />
                      ) : null}
                      <span>{LANG_LABEL(lang)}</span>
                    </Flex>
                  ),
                  children: (
                    <div>
                      {PRODUCT_LANG_FIELDS.map((f) => (
                        <DiffField
                          key={f.key}
                          label={f.label}
                          activeLabel={activeLabel}
                          activeTagColor={activeTagColor}
                          pendingLabel={pendingLabel}
                          pendingTagColor={pendingTagColor}
                          mode={f.mode || 'word'}
                          activeVal={lv(a, lang, f)}
                          pendingVal={lv(p, lang, f)}
                        />
                      ))}
                    </div>
                  ),
                }))}
              />
            </div>
            <DiffChips label="CVP links" active={a.whitelist.cvps || []} pending={p.whitelist.cvps || []} />
            <DiffChips label="Attribute links" active={a.whitelist.attrs || []} pending={p.whitelist.attrs || []} />
            <DiffChips label="Persona links" active={a.whitelist.personas || []} pending={p.whitelist.personas || []} />
            {objectivesEnabled && (
              <DiffField
                label={productOfferingText('objectives.diff_label')}
                activeLabel={activeLabel}
                activeTagColor={activeTagColor}
                pendingLabel={pendingLabel}
                pendingTagColor={pendingTagColor}
                mode="line"
                activeVal={objectiveLines(a)}
                pendingVal={objectiveLines(p)}
              />
            )}
            {audiencesEnabled && (
              <React.Fragment>
                <DiffChips
                  label={audienceText('diff_label')}
                  active={activeAudiences.map(({ audienceKey }) => audienceKey)}
                  pending={pendingAudiences.map(({ audienceKey }) => audienceKey)}
                  labelForValue={audienceLabelForKey}
                />
                {changedAudienceReasons.map((association) => {
                  const activeAssociation = activeAudienceByKey.get(association.audienceKey);
                  return (
                    <DiffField
                      key={`audience-reason-${association.audienceKey}`}
                      label={audienceText('reason_diff_label', {
                        audience: audienceLabelForKey(association.audienceKey),
                      })}
                      activeLabel={activeLabel}
                      activeTagColor={activeTagColor}
                      pendingLabel={pendingLabel}
                      pendingTagColor={pendingTagColor}
                      activeVal={normalizeAudienceReason(
                        activeAssociation ? activeAssociation.reasonToUse : '',
                      )}
                      pendingVal={normalizeAudienceReason(association.reasonToUse)}
                    />
                  );
                })}
                {changedAudienceLangs.map((association) => {
                  const activeAssociation = activeAudienceByKey.get(association.audienceKey);
                  return (
                    <React.Fragment key={`audience-lang-${association.audienceKey}`}>
                      <DiffField
                        label={audienceText('lang_diff_label', {
                          audience: audienceLabelForKey(association.audienceKey),
                        })}
                        activeLabel={activeLabel}
                        activeTagColor={activeTagColor}
                        pendingLabel={pendingLabel}
                        pendingTagColor={pendingTagColor}
                        activeVal={activeAssociation ? activeAssociation.lang : ''}
                        pendingVal={association.lang}
                      />
                      {/* Annotation only, on a cleared language: the DiffField
                          values stay raw, so an association untargeted on both
                          sides never renders a row and the country default
                          never reads as a pending change. */}
                      {!association.lang && Boolean(inheritedLang) && (
                        <Flex align="center" gap={4} wrap="wrap" className="cc-audience-lang-default">
                          <DefaultTag source="country" />
                          <Typography.Metadata>{LANG_LABEL(inheritedLang)}</Typography.Metadata>
                        </Flex>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            )}
          </div>
        );
      })()}
    </Modal>
  );
};

export const ProductOfferingDetail = ({
  activeLang,
  pendingActionsDisabledReason = '',
  attrOptions,
  audienceCiEnabledLangs = [],
  audienceLabelForKey = (key) => key,
  audienceLangOptions = [],
  audienceListFailed = false,
  audienceListLoaded = false,
  audienceListLoading = false,
  audienceOptions = [],
  audienceValidationUnavailable = false,
  audiencesEnabled = false,
  base,
  busy,
  canCreate,
  canEditLocally,
  canReview,
  canUpdate,
  country,
  countryDefaultLang = () => '',
  countryName,
  cvpOptions,
  defaultLang,
  deeplinkVal,
  discardDraft,
  discardLocalEdits,
  draftReady,
  hasEdits,
  hasInvalidDeeplink = false,
  hasInvalidAudienceLangs = false,
  hasInvalidAudienceReasons = false,
  hasInvalidAudiences = false,
  hasPending,
  isDraft,
  isMine,
  generationDisabledReason,
  langVal,
  langs,
  listVal,
  nameVal,
  objectivesEnabled = false,
  onApprove,
  onGenerate,
  onEditChanges,
  onRecall,
  onReject,
  onRetryAudiences,
  onSendForApproval,
  pendingAuthor,
  pendingExpiry,
  personaOptions,
  productNameReady,
  sel,
  setActiveLang,
  setDeeplinkEdit,
  setLang,
  setList,
  setNameEdit,
  setShowDiff,
  setShowPending,
  setVerticalEdit,
  showDiff,
  showPending,
  verticalDeeplink = () => '',
  verticalName,
  verticals,
  verticalVal,
}) => {
  if (!sel || !base) {
    return (
      <Empty description={`Add a Product Offering to start editing Mystique inputs for ${country}.`} />
    );
  }

  let chip = { tone: 'active', label: 'ACTIVE' };
  if (isDraft) chip = { tone: 'draft', label: 'DRAFT' };
  else if (hasPending) chip = { tone: 'pending', label: 'PENDING APPROVAL' };
  else if (hasEdits) chip = { tone: 'draft', label: 'LOCAL EDITS' };

  // Editing is open to any viewer — a draft and its edits stay in browser
  // state, and cpCreateProduct/cpUpdateProduct only run from Send for approval.
  const canEdit = canEditLocally;
  const canSubmit = isDraft ? canCreate : canUpdate;
  const canSubmitForApproval = productNameReady && canSubmitProductForApproval(
    { canCreate, canUpdate },
    {
      isDraft,
      draftReady,
      hasActive: Boolean(sel.active),
      hasEdits,
      hasPending,
      hasInvalidDeeplink,
      hasInvalidAudiences,
      hasInvalidAudienceReasons,
      hasInvalidAudienceLangs,
    },
  );
  const canRecallChange = canRecallPendingChange({ canUpdate }, isMine);
  const canReviewChange = canReviewPendingChange({ canReview }, isMine);

  let sendTip = '';
  if (!canSubmit) {
    sendTip = isDraft
      ? "You don't have permission to create product offerings."
      : "You don't have permission to update product offerings.";
  } else if (hasPending) sendTip = 'A pending change already exists - resolve it first.';
  else if (!productNameReady) sendTip = 'Enter the tagged name.';
  else if (isDraft && !draftReady) sendTip = `Enter the ${LANG_LABEL(defaultLang)} name & product overview`;
  else if (!isDraft && !sel.active) sendTip = 'This offering has no active version to update.';
  else if (hasInvalidDeeplink) sendTip = productOfferingText('validation.deeplink');
  else if (audienceValidationUnavailable) sendTip = audienceText('validation_unavailable');
  else if (hasInvalidAudiences) sendTip = audienceText('invalid_submission');
  else if (hasInvalidAudienceLangs) sendTip = audienceText('lang_invalid_submission');
  else if (hasInvalidAudienceReasons) {
    sendTip = audienceText('reason_too_long', { max: MAX_AUDIENCE_REASON_LENGTH });
  }

  // Shown as the deep-link placeholder, so it is visible precisely while the
  // field is empty — an empty string keeps the generic placeholder instead.
  const inheritedDeeplink = verticalDeeplink(verticalVal());
  const showsVerticalDefault = !deeplinkVal() && Boolean(inheritedDeeplink);
  // What Content Injection would resolve for this offering, ignoring the
  // campaign Audience template, which the editor has no context for. A
  // whitespace-only entry cannot be saved, so it falls back to the default.
  const effectiveDeeplink = normalizeProductDeeplink(deeplinkVal()) || inheritedDeeplink;
  // The language an untargeted Audience association generates in. A Product
  // Offering never changes country, so one value serves every version shown.
  const inheritedLang = countryDefaultLang(country);

  const canShowDiff = (sel.active && sel.pending) || isDraft || (hasEdits && sel.active);
  const comparisonByLang = {};
  if (isDraft || (hasEdits && sel.active)) {
    langs.forEach((lang) => {
      const name = langVal(lang, 'name');
      const overview = langVal(lang, 'overview');
      comparisonByLang[lang] = {
        name,
        ciEnabled: Boolean(langVal(lang, 'ciEnabled')) && Boolean(name.trim() && overview.trim()),
        overview,
        reason: langVal(lang, 'reason'),
        cta: langVal(lang, 'cta'),
      };
    });
  }
  const comparisonDiff = isDraft || (hasEdits && sel.active) ? {
    name: nameVal(),
    vertical: verticalVal(),
    deeplink: deeplinkVal(),
    byLang: comparisonByLang,
    whitelist: {
      cvps: listVal('cvps'),
      attrs: listVal('attrs'),
      personas: listVal('personas'),
      audiences: listVal('audiences'),
      objectives: listVal('objectives'),
    },
  } : null;

  return (
    <div>
      {hasPending && sel.active && (
        <Alert
          type="info"
          showIcon
          className="cc-action-bar"
          message={(
            <Flex justify="space-between" align="center" gap={12} wrap="wrap">
              <div className="cc-alert-content">
                <div>
                  Showing <strong>{showPending ? 'PENDING APPROVAL' : 'ACTIVE'}</strong> version.
                  {' '}Edits here stay local and can be used to generate content, but they
                  {' '}can&apos;t be submitted until the pending change is resolved.
                </div>
                <div>
                  Changes were made by <strong>{pendingAuthor || '—'}</strong>
                  {pendingExpiry
                    ? (
                      <React.Fragment>
                        {' '}and will expire at <strong>{pendingExpiry}</strong>
                      </React.Fragment>
                    )
                    : null}.
                </div>
              </div>
              <Button size="small" onClick={() => setShowPending((v) => !v)}>
                {showPending ? 'See active' : 'See changes'}
              </Button>
            </Flex>
          )}
        />
      )}

      <Flex vertical gap={4} className="cc-detail-header">
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Title level={4} className="cc-title-reset">{nameVal() || productDisplayName(base, activeLang)}</Typography.Title>
          <Tag>{countryName(country)}</Tag>
          <Select
            value={verticalVal()}
            onChange={setVerticalEdit}
            disabled={!canEdit}
            className="cc-vertical-select"
            options={verticals.map((v) => ({ value: v.id, label: v.name }))}
          />
        </Flex>
        <Typography.Metadata type="secondary">{isDraft ? 'New draft - not saved yet' : `Updated by ${nameFromEmail(base.updatedBy || base.createdBy) || '—'}`}</Typography.Metadata>
      </Flex>

      <Flex align="center" justify="space-between" wrap="wrap" gap={12} className="cc-action-bar">
        <Flex align="center" gap={4}>
          <Tag color={PRODUCT_STATUS_TAG[chip.tone].color}>{chip.label}</Tag>
          {hasPending && !sel.active && <Tag color="warning">NEW</Tag>}
        </Flex>
        <Flex gap={8} align="center" wrap="wrap">
          {canShowDiff && (
            <Button size="small" onClick={() => setShowDiff(true)}>Show diff</Button>
          )}
          <Tooltip title={generationDisabledReason}>
            <Button
              size="small"
              icon={<PlaybackSpeedUniversal />}
              disabled={Boolean(generationDisabledReason)}
              onClick={onGenerate}
            >
              Generate content
            </Button>
          </Tooltip>
          {isDraft && (
            <Button size="small" icon={<UndoUniversal />} danger onClick={discardDraft}>Discard draft</Button>
          )}
          {hasEdits && !isDraft && (
            <Button size="small" icon={<UndoUniversal />} danger onClick={discardLocalEdits}>Discard local edits</Button>
          )}
          {hasPending && canRecallChange && (
            <React.Fragment>
              <Tooltip title={pendingActionsDisabledReason}>
                <Button
                  size="small"
                  icon={<UndoUniversal />}
                  danger
                  loading={busy}
                  disabled={Boolean(pendingActionsDisabledReason)}
                  onClick={onRecall}
                >
                  Recall
                </Button>
              </Tooltip>
              <Tooltip title={pendingActionsDisabledReason}>
                <Button
                  size="small"
                  loading={busy}
                  disabled={Boolean(pendingActionsDisabledReason)}
                  onClick={onEditChanges}
                >
                  {editChangesText('action')}
                </Button>
              </Tooltip>
            </React.Fragment>
          )}
          {hasPending && canReviewChange && (
            <React.Fragment>
              <Button size="small" type="primary" danger icon={<CloseUniversal />} loading={busy} onClick={onReject}>Reject</Button>
              <Button size="small" type="primary" icon={<TickMarkOutlined />} loading={busy} onClick={onApprove}>Approve</Button>
            </React.Fragment>
          )}
          <Tooltip title={sendTip}>
            <Button
              size="small"
              type="primary"
              icon={<TickMarkOutlined />}
              loading={busy}
              disabled={!canSubmitForApproval}
              onClick={onSendForApproval}
            >
              Send for approval
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      <Card bordered={false} className="cc-section-gap">
        <div className="cc-product-header-fields">
          <div>
            <FieldLabel required>Tagged Name</FieldLabel>
            <Input
              value={nameVal()}
              onChange={(e) => setNameEdit(e.target.value)}
              placeholder="Tagged name..."
              disabled={!canEdit}
            />
          </div>
          <div>
            <Flex align="center" justify="space-between" gap={4} wrap="wrap">
              <Flex align="center" gap={4} wrap="wrap">
                <FieldLabel>{productOfferingText('fields.deeplink')}</FieldLabel>
                {showsVerticalDefault && <DefaultTag source="vertical" />}
              </Flex>
              <DeeplinkQr deeplink={effectiveDeeplink} label={nameVal()} />
            </Flex>
            <Input
              id="product-deeplink"
              value={deeplinkVal()}
              onChange={(e) => setDeeplinkEdit(e.target.value)}
              placeholder={inheritedDeeplink || productOfferingText('placeholders.deeplink')}
              disabled={!canEdit}
              status={hasInvalidDeeplink ? 'error' : undefined}
              aria-invalid={hasInvalidDeeplink}
              aria-describedby="product-deeplink-validation"
            />
            {hasInvalidDeeplink && (
              <Typography.Metadata id="product-deeplink-validation" type="danger">
                {productOfferingText('validation.deeplink')}
              </Typography.Metadata>
            )}
          </div>
        </div>
      </Card>

      <ProductLanguageTabs
        activeLang={activeLang}
        disabled={!canEdit}
        langVal={langVal}
        langs={langs}
        setActiveLang={setActiveLang}
        setLang={setLang}
      />

      <div className="cc-section-gap">
        <ProductLinks
          attrOptions={attrOptions}
          audienceCiEnabledLangs={audienceCiEnabledLangs}
          audienceInheritedLang={inheritedLang}
          audienceLangOptions={audienceLangOptions}
          audienceListFailed={audienceListFailed}
          audienceListLoaded={audienceListLoaded}
          audienceListLoading={audienceListLoading}
          audienceOptions={audienceOptions}
          audiencesEnabled={audiencesEnabled}
          cvpOptions={cvpOptions}
          disabled={!canEdit}
          listVal={listVal}
          objectivesEnabled={objectivesEnabled}
          onRetryAudiences={onRetryAudiences}
          personaOptions={personaOptions}
          setList={setList}
        />
      </div>

      <ProductDiffModal
        audienceLabelForKey={audienceLabelForKey}
        audiencesEnabled={audiencesEnabled}
        pendingActionsDisabledReason={pendingActionsDisabledReason}
        busy={busy}
        canReview={canReview}
        canUpdate={canUpdate}
        comparisonDiff={comparisonDiff}
        isMine={isMine}
        langs={langs}
        objectivesEnabled={objectivesEnabled}
        generationDisabledReason={generationDisabledReason}
        inheritedLang={inheritedLang}
        onApprove={onApprove}
        onCancel={() => setShowDiff(false)}
        onGenerate={onGenerate}
        onEditChanges={onEditChanges}
        onRecall={onRecall}
        onReject={onReject}
        open={showDiff}
        sel={sel}
        verticalDeeplink={verticalDeeplink}
        verticalName={verticalName}
      />
    </div>
  );
};

export const NewProductModal = ({
  canEditLocally, countries, defaultCountry, onClose, onCreate, open, verticals,
}) => {
  const [productCountry, setProductCountry] = useState('');
  const [vertical, setVertical] = useState('');
  const [name, setName] = useState('');
  useEffect(() => {
    if (open) {
      setProductCountry(defaultCountry || (countries[0] && countries[0].id) || '');
      setVertical((verticals[0] && verticals[0].id) || '');
      setName('');
    }
  }, [open, countries, defaultCountry, verticals]);

  const canContinue = canEditLocally && Boolean(productCountry && vertical && name.trim());
  const handleCreate = () => {
    if (!canContinue) return;
    onCreate({
      country: productCountry,
      name: name.trim(),
      vertical,
      deeplink: '',
      byLang: {},
      whitelist: {
        cvps: [], attrs: [], personas: [], audiences: [], objectives: [],
      },
    });
  };
  let createTip = '';
  if (!canEditLocally) createTip = "You don't have permission to edit product offerings.";
  else if (!canContinue) createTip = 'Choose a country, vertical and enter the tagged name';

  return (
    <Modal
      title="Create Product Offering"
      open={open}
      onCancel={onClose}
      width={520}
      className="cc-modal"
      footer={(
        <Flex justify="space-between" align="center" gap={12}>
          <Typography.Metadata type="secondary">You&apos;ll add content, languages &amp; links next, then send for approval.</Typography.Metadata>
          <Flex gap={8}>
            <Button onClick={onClose}>Cancel</Button>
            <Tooltip title={createTip}>
              <Button type="primary" disabled={!canContinue} onClick={handleCreate}>Continue to editor</Button>
            </Tooltip>
          </Flex>
        </Flex>
      )}
    >
      <div className="cc-field">
        <FieldLabel required>Country</FieldLabel>
        <Select disabled={!canEditLocally} value={productCountry} onChange={setProductCountry} className="cc-full-width" options={countries.map((c) => ({ value: c.id, label: c.name }))} />
      </div>
      <div className="cc-field">
        <FieldLabel required>Vertical</FieldLabel>
        <Select disabled={!canEditLocally} value={vertical} onChange={setVertical} className="cc-full-width" options={verticals.map((v) => ({ value: v.id, label: v.name }))} />
      </div>
      <div className="cc-field">
        <FieldLabel required>Tagged Name</FieldLabel>
        <Input
          disabled={!canEditLocally}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onPressEnter={() => { if (canContinue) handleCreate(); }}
          placeholder="Tagged name..."
        />
      </div>
    </Modal>
  );
};
